import {
  collection,
  query,
  where,
  getDocs,
  getDoc,
  doc,
  setDoc,
  updateDoc,
  addDoc,
  serverTimestamp,
  increment,
  orderBy
} from "firebase/firestore";
import { db } from "../firebase";

export const DEFAULT_REFERRAL_SETTINGS = {
  enabled: true,
  discountType: "percentage", // "percentage" | "fixed"
  refereeDiscount: 10, // 10% off first purchase for referred friend
  maxDiscount: 10000, // Maximum cap in Naira for percentage discounts
  minOrderAmount: 5000, // Min order amount in Naira
  referrerReward: 1000, // ₦1,000 reward/credit per qualified referral
  programTitle: "Afriport Friends & Family Referral Program",
  programDescription: "Share your love for clean solar & power tech! Give your friends 10% off their first order, and earn ₦1,000 rewards.",
};

/**
 * Generates a clean, memorable referral code.
 * e.g., AFR-JOHN8A or AFR-87B3F
 */
export function generateReferralCode(profile, uid) {
  const namePart = (profile?.fullName || "")
    .replace(/[^a-zA-Z]/g, "")
    .slice(0, 4)
    .toUpperCase();
  const randPart = Math.random().toString(36).substring(2, 6).toUpperCase();
  const fallback = uid ? uid.slice(0, 5).toUpperCase() : randPart;
  return `AFR-${namePart || fallback}${randPart.slice(0, 2)}`;
}

/**
 * Returns the shareable link for a given referral code.
 */
export function getReferralLink(referralCode) {
  if (!referralCode) return "";
  const origin = typeof window !== "undefined" && window.location?.origin
    ? window.location.origin
    : "https://afriportb2c.online";
  return `${origin}/register?ref=${referralCode}`;
}

/**
 * Captures and stores an incoming referral code in browser storage.
 */
export function storeIncomingReferral(refCode) {
  if (!refCode || typeof refCode !== "string") return;
  const clean = refCode.trim().toUpperCase();
  if (clean.length < 3) return;
  try {
    localStorage.setItem("afriport_referral_code", clean);
    sessionStorage.setItem("afriport_referral_code", clean);
  } catch (e) {
    console.warn("Storage not available for referral code:", e);
  }
}

/**
 * Retrieves any stored referral code from previous visits or URL parameters.
 */
export function getStoredReferral() {
  try {
    return (
      localStorage.getItem("afriport_referral_code") ||
      sessionStorage.getItem("afriport_referral_code") ||
      ""
    );
  } catch (e) {
    return "";
  }
}

/**
 * Clears stored referral code after it has been used.
 */
export function clearStoredReferral() {
  try {
    localStorage.removeItem("afriport_referral_code");
    sessionStorage.removeItem("afriport_referral_code");
  } catch (e) {
    // Ignore
  }
}

/**
 * Ensures a user profile in Firestore has a valid referral code.
 * If missing, generates and saves one, returning the code.
 */
export async function ensureUserReferralCode(user, currentProfile) {
  if (currentProfile?.referralCode) return currentProfile.referralCode;
  if (!user?.uid) return "";

  const newCode = generateReferralCode(currentProfile, user.uid);
  try {
    await updateDoc(doc(db, "users", user.uid), {
      referralCode: newCode,
    });
  } catch (err) {
    console.warn("Could not backfill referral code:", err?.message);
  }
  return newCode;
}

/**
 * Validates a referral code against existing users in Firestore.
 */
export async function validateReferralCode(code) {
  if (!code?.trim()) return { valid: false, error: "Enter a referral code" };
  const clean = code.trim().toUpperCase();

  try {
    const snap = await getDocs(
      query(collection(db, "users"), where("referralCode", "==", clean))
    );
    if (snap.empty) {
      return { valid: false, error: `Referral code "${clean}" is not recognized` };
    }
    const referrerDoc = snap.docs[0];
    const data = referrerDoc.data();
    return {
      valid: true,
      referrerId: referrerDoc.id,
      referrerName: data.fullName || "Afriport Member",
      referrerEmail: data.email || "",
      code: clean,
    };
  } catch (err) {
    console.error("Error validating referral code:", err);
    return { valid: false, error: "Unable to verify referral code" };
  }
}

/**
 * Records a referral when a new user registers with a referrer's code.
 */
export async function recordReferralSignup({ referrerId, referrerName, referrerEmail, refereeId, refereeName, refereeEmail, code }) {
  try {
    // 1. Create referral record
    await addDoc(collection(db, "referrals"), {
      referrerId,
      referrerName: referrerName || "Afriport Member",
      referrerEmail: referrerEmail || "",
      refereeId,
      refereeName: refereeName || "New Member",
      refereeEmail: refereeEmail || "",
      referralCode: code,
      status: "registered", // "registered" | "completed_purchase"
      rewardClaimed: false,
      createdAt: serverTimestamp(),
    });

    // 2. Increment referrer's referral count
    await updateDoc(doc(db, "users", referrerId), {
      referralsCount: increment(1),
    });
  } catch (err) {
    console.warn("Could not record referral signup:", err?.message);
  }
}

/**
 * Fetches referrals made by a specific user.
 */
export async function getUserReferrals(userId) {
  if (!userId) return [];
  try {
    const q = query(
      collection(db, "referrals"),
      where("referrerId", "==", userId)
    );
    const snap = await getDocs(q);
    const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    // Client-side sort by createdAt descending
    return list.sort((a, b) => {
      const timeA = a.createdAt?.toDate ? a.createdAt.toDate().getTime() : 0;
      const timeB = b.createdAt?.toDate ? b.createdAt.toDate().getTime() : 0;
      return timeB - timeA;
    });
  } catch (err) {
    console.error("Error fetching user referrals:", err);
    return [];
  }
}

/**
 * Fetches all referrals for Admin overview.
 */
export async function getAllReferrals() {
  try {
    const q = query(
      collection(db, "referrals"),
      orderBy("createdAt", "desc")
    );
    const snap = await getDocs(q);
    return snap.docs.map(d => ({ id: d.id, ...d.data() }));
  } catch (err) {
    // Fallback without ordering if index is missing
    const snap = await getDocs(collection(db, "referrals"));
    const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    return list.sort((a, b) => {
      const timeA = a.createdAt?.toDate ? a.createdAt.toDate().getTime() : 0;
      const timeB = b.createdAt?.toDate ? b.createdAt.toDate().getTime() : 0;
      return timeB - timeA;
    });
  }
}

/**
 * Loads referral program global settings.
 */
export async function getReferralSettings() {
  try {
    const snap = await getDoc(doc(db, "referral_settings", "general"));
    if (snap.exists()) {
      return { ...DEFAULT_REFERRAL_SETTINGS, ...snap.data() };
    }
    return DEFAULT_REFERRAL_SETTINGS;
  } catch (err) {
    console.warn("Could not load referral settings, using defaults:", err);
    return DEFAULT_REFERRAL_SETTINGS;
  }
}

/**
 * Saves referral program global settings (Admin only).
 */
export async function saveReferralSettings(settings) {
  await setDoc(
    doc(db, "referral_settings", "general"),
    {
      ...settings,
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );
}
